import React, { useEffect, useState } from "react";
import api, { imageUrl } from "../lib/api";
import { Plus, Trash2, Save, Eye, EyeOff, Pencil } from "lucide-react";

const CATEGORIES = [
  ["planning-and-contractors", "Planning & Choosing a Contractor"],
  ["cost-and-financing", "Cost & Financing"],
  ["basement-development", "Basement Development"],
  ["legal-basement-suites", "Legal Basement Suites"],
  ["kitchen-renovations", "Kitchen Renovations"],
  ["bathroom-renovations", "Bathroom Renovations"],
  ["whole-home-and-additions", "Whole-Home & Additions"],
  ["permits-codes-inspections", "Permits, Codes & Inspections"],
  ["commercial-renovations", "Commercial Renovations"],
  ["climate-and-local", "Climate & Local Insights"],
];
const catName = (s) => (CATEGORIES.find((c) => c[0] === s) || [null, s])[1];

const empty = { title: "", category: "planning-and-contractors", excerpt: "", body: "", cover: "", tags: "", read_time: 5, published: true, order: 0 };

export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setForm(empty); setEditingId(null); };

  const payload = () => ({
    title: form.title,
    category: form.category,
    excerpt: form.excerpt,
    body: form.body,
    cover: form.cover,
    tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
    read_time: Number(form.read_time) || 5,
    published: !!form.published,
    order: Number(form.order) || 0,
  });

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { alert("Title and body are required"); return; }
    setSaving(true);
    try {
      if (editingId) await api.put(`/admin/blog/${editingId}`, payload());
      else await api.post("/admin/blog", payload());
      reset();
      load();
    } catch (e) {
      alert("Could not save. Please try again.");
    } finally { setSaving(false); }
  };

  const edit = async (p) => {
    setForm({ ...p, tags: (p.tags || []).join(", ") });
    setEditingId(p.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/blog/${id}`);
    load();
  };

  const uploadCover = async (file) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const res = await api.post("/admin/upload", { data: reader.result, content_type: file.type });
      set("cover", res.data.url);
    };
    reader.readAsDataURL(file);
  };

  const inputCls = "w-full px-4 py-3 rounded-lg bg-[#0b0e13] border border-white/10 text-white focus:border-[#2f9fe6] outline-none transition-colors";
  const shown = filter === "all" ? posts : posts.filter((p) => p.category === filter);

  return (
    <div className="max-w-5xl">
      <h1 className="text-white font-display text-4xl font-bold mb-1">Blog</h1>
      <p className="text-[#6b7683] mb-8">Manage SEO articles shown on your website's blog.</p>

      <div className="bg-[#141a22] rounded-xl p-6 border border-white/5 mb-10">
        <h2 className="text-white font-semibold mb-4">{editingId ? "Edit article" : "Add an article"}</h2>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <input className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Article title" data-testid="blog-title" />
          <select className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="blog-category">
            {CATEGORIES.map(([slug, name]) => <option key={slug} value={slug}>{name}</option>)}
          </select>
        </div>
        <textarea rows={2} className={`${inputCls} mb-4`} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} placeholder="Short excerpt / meta description" data-testid="blog-excerpt" />
        <textarea rows={12} className={`${inputCls} mb-2 font-mono text-sm`} value={form.body} onChange={(e) => set("body", e.target.value)} placeholder="Article body — HTML supported (use <h2>, <p>, <ul>, <a href='/services'> …)" data-testid="blog-body-input" />
        <p className="text-[#6b7683] text-xs mb-4">Tip: use &lt;h2&gt; for headings, &lt;p&gt; for paragraphs, &lt;ul&gt;&lt;li&gt; for lists, and &lt;a href="/services/kitchen"&gt; for internal links.</p>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-sm text-[#9aa4af] block mb-1">Cover image</label>
            <input className={inputCls} value={form.cover || ""} onChange={(e) => set("cover", e.target.value)} placeholder="Image URL or upload below" />
            <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && uploadCover(e.target.files[0])} className="mt-2 text-sm text-[#9aa4af]" />
            {form.cover && <img src={imageUrl(form.cover)} alt="cover preview" className="mt-2 w-full h-32 object-cover rounded-lg" />}
          </div>
          <div className="space-y-4">
            <input className={inputCls} value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="Tags (comma separated)" />
            <input type="number" className={inputCls} value={form.read_time} onChange={(e) => set("read_time", e.target.value)} placeholder="Read time (minutes)" />
            <label className="flex items-center gap-3 text-[#c7ced5] cursor-pointer">
              <input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} className="w-4 h-4 accent-[#2f9fe6]" />
              Published (visible on site)
            </label>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#2f9fe6] text-white font-semibold hover:bg-[#1f82c7] disabled:opacity-60" data-testid="blog-save"><Save className="w-4 h-4" /> {saving ? "Saving…" : editingId ? "Update" : "Add"}</button>
          {editingId && <button onClick={reset} className="px-4 py-2.5 text-[#9aa4af] hover:text-white">Cancel</button>}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button onClick={() => setFilter("all")} className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === "all" ? "bg-[#2f9fe6] text-white" : "bg-white/5 text-[#9aa4af]"}`}>All ({posts.length})</button>
        {CATEGORIES.map(([slug, name]) => {
          const n = posts.filter((p) => p.category === slug).length;
          return <button key={slug} onClick={() => setFilter(slug)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === slug ? "bg-[#2f9fe6] text-white" : "bg-white/5 text-[#9aa4af]"}`}>{name} ({n})</button>;
        })}
      </div>

      {loading ? <div className="text-[#6b7683]">Loading…</div> : (
        <div className="space-y-3">
          {shown.map((p) => (
            <div key={p.id} className="bg-[#141a22] rounded-xl p-4 border border-white/5 flex items-center gap-4">
              {p.cover && <img src={imageUrl(p.cover)} alt="" className="w-16 h-16 object-cover rounded-lg shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold text-sm truncate">{p.title}</div>
                <div className="text-[#6b7683] text-xs mt-0.5">{catName(p.category)} · {p.read_time} min {p.published ? "" : "· (draft)"}</div>
              </div>
              <span className={`shrink-0 ${p.published ? "text-[#2f9fe6]" : "text-[#6b7683]"}`}>{p.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</span>
              <button onClick={() => edit(p)} className="px-3 py-2 rounded-lg bg-white/5 text-white text-sm hover:bg-white/10 flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Edit</button>
              <button onClick={() => remove(p.id)} className="px-3 py-2 rounded-lg bg-white/5 text-[#9aa4af] hover:text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
